import React, { useEffect, useState } from 'react';
import { Grid, Typography } from '@mui/material';
import GoalCardStudent from '../../GlobalComponents/GoalCards/GoalCardStudent';
import { getUserDetails } from '../../../src/storageService';
import { getStudentGoalsAPI } from '../../Utils/apiService';
import KenLoader from '../../GlobalComponents/KenLoader';

type SubGoal = {
  id: string;
  Name: string;
  description: string;
  startDate: string;
  endDate: string;
};

type Goal = {
  Id?: any;
  title?: string;
  desc?: string;
  description?: string;
  type?: string;
  EndDate?: string;
  StartDate?: string;
  goalType?: string;
  subgoals: SubGoal[];
  progress?: { done: number; total: number } | null;
  status?: string;
};


const transformGoal = (goal: any): Goal => {
  return {
    Id: goal.Id,
    title: goal.title || '',
    desc: goal.description || '',
    description: goal.description || '',
    type: goal.type || 'Unknown',
    StartDate: goal.StartDate || '',
    EndDate: goal.EndDate || '',
    goalType: goal.goalType || '',
    status: goal.status || '',
    progress:
      typeof goal.progress === 'object'
        ? goal.progress
        : typeof goal.progress === 'string'
        ? { done: parseInt(goal.progress), total: 100 }
        : { done: 0, total: 0 },
    subgoals: Array.isArray(goal.subGoals)
      ? goal.subGoals.map((s: any) => ({
          id: s.Id || '',
          Name: s.Name || '',
          description: s.description || '',
          startDate: s.startDate || '',
          endDate: s.endDate || '',
        }))
      : [],
  };
};

export const AssignedGoals: React.FC = () => {
  const [assignedGoals, setAssignedGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);

      try {
        const userDetails = getUserDetails();
        const contactId = userDetails?.ContactId;

        if (!contactId) {
          setError('No contactId found');
          setLoading(false);
          return;
        }

        const response = await getStudentGoalsAPI(contactId);
        console.log('AssignedGoals API Response:', response);

        if (response.success && Array.isArray(response.data?.assignedGoals)) {
          const transformed = response.data.assignedGoals.map(transformGoal);
          setAssignedGoals(transformed);
        } else {
          setAssignedGoals([]);
        }
      } catch (err: any) {
        setError(err.message || 'Error fetching assigned goals');
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, []);

  if (loading) return <KenLoader />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (assignedGoals.length === 0) return <Typography>No assigned goals found.</Typography>;

  return (
    <Grid container spacing={2}>
      {assignedGoals.map((g, i) => (
        <Grid item xs={12} sm={6} key={i}>
          <GoalCardStudent
            Id={g.Id}
            title={g.title ?? 'Untitled Goal'}
            description={g.description ?? g.desc ?? 'No description'}
            type={g.type ?? 'Unknown'}
            progress={g.progress ?? { done: 0, total: 0 }}
            assignedGoals=""
            subgoals={g.subgoals ?? []}
            startdate={g.StartDate}
            enddate={g.EndDate}
            goalType={g.goalType}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export const MyGoals: React.FC = () => {
  const [myGoals, setMyGoals] = useState<Goal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);

      try {
        const userDetails = getUserDetails();
        const contactId = userDetails?.ContactId;

        if (!contactId) {
          setError('No contactId found');
          setLoading(false);
          return;
        }

        const response = await getStudentGoalsAPI(contactId);
        console.log('MyGoals API Response:', response);

        if (response.success && Array.isArray(response.data?.myGoals)) {
          const transformed = response.data.myGoals.map(transformGoal);
          setMyGoals(transformed);
        } else {
          setMyGoals([]);
        }
      } catch (err: any) {
        setError(err.message || 'Error fetching my goals');
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, []);

  if (loading) return <KenLoader />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (myGoals.length === 0) return <Typography>No goals found in your list.</Typography>;

  return (
    <Grid container spacing={2}>
      {myGoals.map((g, i) => (
        <Grid item xs={12} sm={6} key={i}>
          <GoalCardStudent
            Id={g.Id}
            title={g.title ?? 'Untitled Goal'}
            description={g.description ?? g.desc ?? 'No description'}
            type={g.type ?? 'Unknown'}
            progress={g.progress ?? { done: 0, total: 0 }}
            assignedGoals="mygoals"
            subgoals={g.subgoals ?? []}
            startdate={g.StartDate}
            enddate={g.EndDate}
            goalType={g.goalType}
            isFaculty={false}
            isOnlyFaculty={false}
          />
        </Grid>
      ))}
    </Grid>
  );
};
